/**
 * 服务端发布操作(publish_operations)在浏览器这一侧的状态 —— 契约见
 * docs/CONTRACT-publish-ops.md §1 / §2 / §5 / §6。
 *
 * 一条 op = 一篇稿子发往一个渠道的一次尝试。真源在 chatbot-api:状态每变一次,代理推一帧
 * `{type:'publish_op', sessionId, op}`;帧不缓冲,所以连上/重连/打开会话时再用 REST 补齐。
 * 两条来路都只做一件事:按 `op.id` 合并进本会话的 map。
 *
 * 合并规则(防乱序):新帧覆盖旧状态,但**更旧的**那条不许覆盖更新的 —— 先比 `updatedAt`;
 * 时间相同(服务端是秒级,认领和出结果可能落在同一秒)再比状态的先后,状态只会往前走
 * (requested → executing → unconfirmed → succeeded/failed → abandoned)。
 * REST 快照和帧走同一个函数,所以「快照比帧晚到、但内容比帧旧」也不会把状态拨回去。
 */

export type PublishOpStatus =
  'requested' | 'executing' | 'succeeded' | 'failed' | 'unconfirmed' | 'cancelled' | 'abandoned';

const KNOWN_STATUSES: readonly PublishOpStatus[] = [
  'requested',
  'executing',
  'succeeded',
  'failed',
  'unconfirmed',
  'cancelled',
  'abandoned',
];

export interface PublishOp {
  id: string;
  requestId?: string | null;
  source?: string | null;
  conversationId?: string | null;
  taskRunId?: string | null;
  pieceId?: string | null;
  /** 草稿行 id(uuid)。没有草稿行的发布为 null。 */
  draftId?: string | null;
  /** CREATE_CONTENT_DRAFT 的 draft_id —— 聊天里的卡片按它认领。 */
  clientDraftId?: string | null;
  draftRevision?: number | null;
  channel: string;
  title?: string | null;
  status: PublishOpStatus;
  attempt?: number | null;
  retryOf?: string | null;
  resultUrl?: string | null;
  externalId?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  /**
   * `scope_missing` 时可以把商家直接送去授权的 Shopify 链接,以及缺的权限。服务端目前不发这两项
   * (ShopifyScopeException 的 data 里有,PublishErrorClassifier 没带进 op);有就用,没有前端现取。
   */
  authorizeUrl?: string | null;
  missingScopes?: string[] | null;
  verifiedBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  finishedAt?: string | null;
}

/** 一个会话里所有已知的 op,按 id。 */
export type PublishOpsState = Record<string, PublishOp>;

export const EMPTY_PUBLISH_OPS: PublishOpsState = Object.freeze({}) as PublishOpsState;

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function strList(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const list = v.filter((s): s is string => typeof s === 'string' && !!s.trim());
  return list.length > 0 ? list : null;
}

/**
 * 把一份来路不明的 op(帧里的、REST 里的)收成 {@link PublishOp};形状不对返回 null。
 *
 * `deleted` 按契约永远不会出现;万一出现,当作「这一条不存在」—— 返回带 deleted 标记的
 * 结果交给合并函数删掉,而不是画出来。
 */
export function parsePublishOp(raw: unknown): (PublishOp & { deleted?: boolean }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o.id);
  const channel = str(o.channel);
  const status = str(o.status);
  if (!id || !channel || !status) return null;
  if (status === 'deleted') {
    return { id, channel, status: 'cancelled', deleted: true };
  }
  if (!(KNOWN_STATUSES as readonly string[]).includes(status)) return null;
  return {
    id,
    requestId: str(o.requestId),
    source: str(o.source),
    conversationId: str(o.conversationId),
    taskRunId: str(o.taskRunId),
    pieceId: str(o.pieceId),
    draftId: str(o.draftId),
    clientDraftId: str(o.clientDraftId),
    draftRevision: typeof o.draftRevision === 'number' ? o.draftRevision : null,
    channel,
    title: str(o.title),
    status: status as PublishOpStatus,
    attempt: typeof o.attempt === 'number' ? o.attempt : null,
    retryOf: str(o.retryOf),
    resultUrl: str(o.resultUrl),
    externalId: str(o.externalId),
    errorCode: str(o.errorCode),
    errorMessage: str(o.errorMessage),
    // op 其余字段都是 camelCase;CampaignCard 等工具结果里同一样东西叫 authorize_url / missing_scopes,两种都认。
    authorizeUrl: str(o.authorizeUrl) ?? str(o.authorize_url),
    missingScopes: strList(o.missingScopes) ?? strList(o.missing_scopes),
    verifiedBy: str(o.verifiedBy),
    createdAt: str(o.createdAt),
    updatedAt: str(o.updatedAt),
    finishedAt: str(o.finishedAt),
  };
}

/** 状态只会往前走;同一秒里到达的两条靠它分先后。 */
const STATUS_RANK: Record<PublishOpStatus, number> = {
  requested: 0,
  executing: 1,
  unconfirmed: 2,
  succeeded: 3,
  failed: 3,
  cancelled: 3,
  abandoned: 4,
};

function ts(v: string | null | undefined): number {
  const n = v ? Date.parse(v) : NaN;
  return Number.isFinite(n) ? n : NaN;
}

/** `next` 能不能覆盖 `prev`。 */
export function isNewerPublishOp(prev: PublishOp | undefined, next: PublishOp): boolean {
  if (!prev) return true;
  const a = ts(prev.updatedAt);
  const b = ts(next.updatedAt);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    if (b > a) return true;
    if (b < a) return false;
  } else if (Number.isFinite(a) && !Number.isFinite(b)) {
    // 带时间的不让给不带时间的。
    return false;
  }
  return STATUS_RANK[next.status] >= STATUS_RANK[prev.status];
}

function sameOp(a: PublishOp, b: PublishOp): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof PublishOp>;
  for (const k of keys) {
    const x = a[k];
    const y = b[k];
    // missingScopes 是数组:每次解析都是新数组,按内容比。
    if (Array.isArray(x) || Array.isArray(y)) {
      if (JSON.stringify(x ?? null) !== JSON.stringify(y ?? null)) return false;
    } else if (x !== y) return false;
  }
  return true;
}

/**
 * 把若干 op 合并进状态。没有任何变化时原样返回同一个对象(订阅方据此不重渲)。
 */
export function mergePublishOps(state: PublishOpsState, incoming: unknown[]): PublishOpsState {
  let next: PublishOpsState | null = null;
  for (const raw of incoming) {
    const op = parsePublishOp(raw);
    if (!op) continue;
    const cur = (next ?? state)[op.id];
    if (op.deleted) {
      if (cur) {
        next = next ?? { ...state };
        delete next[op.id];
      }
      continue;
    }
    if (!isNewerPublishOp(cur, op) || (cur && sameOp(cur, op))) continue;
    next = next ?? { ...state };
    next[op.id] = op;
  }
  return next ?? state;
}

/** §5.1 查询一次最多返回这么多条(新的在前)。 */
export const PUBLISH_OPS_LIST_LIMIT = 100;

/**
 * 合并一份 REST 快照(§5.1),并在快照**覆盖了这个会话的全部 op** 时,删掉本地有、快照里没有的
 * 那几条 —— 它们在服务端已被逻辑删除(deleted 永远不会以帧或接口返回,快照里缺席是唯一信号)。
 *
 * - 快照条数达到上限(100)时不删:可能只是被截断了,缺席不代表删除。
 * - 只删 `knownBefore` 里的 id —— 也就是发请求**之前**本地就有的。请求在路上时经帧新到的 op
 *   不在快照里是因为快照比它早,不是因为被删了。
 */
export function mergePublishOpsSnapshot(
  state: PublishOpsState,
  snapshot: unknown[],
  knownBefore: ReadonlySet<string>
): PublishOpsState {
  const merged = mergePublishOps(state, snapshot);
  if (snapshot.length >= PUBLISH_OPS_LIST_LIMIT) return merged;
  const present = new Set<string>();
  for (const raw of snapshot) {
    const op = parsePublishOp(raw);
    if (op && !op.deleted) present.add(op.id);
  }
  let next: PublishOpsState | null = null;
  for (const id of Object.keys(merged)) {
    if (present.has(id) || !knownBefore.has(id)) continue;
    next = next ?? { ...merged };
    delete next[id];
  }
  return next ?? merged;
}

// ---------- 展示用的取数 ----------

/** 一张卡/一个进度块认领 op 用的键:clientDraftId 和草稿行 id 都算。 */
export function publishOpDraftKeys(op: PublishOp): string[] {
  return [op.clientDraftId, op.draftId].filter((k): k is string => !!k);
}

function opOrder(op: PublishOp): number {
  const c = ts(op.createdAt);
  return Number.isFinite(c) ? c : ts(op.updatedAt);
}

/**
 * 每个渠道只留最新那一次尝试(重试会产生新 op),按渠道固定顺序排。
 */
export function latestOpPerChannel(ops: PublishOp[]): PublishOp[] {
  const byChannel = new Map<string, PublishOp>();
  for (const op of ops) {
    const cur = byChannel.get(op.channel);
    if (!cur) {
      byChannel.set(op.channel, op);
      continue;
    }
    const a = opOrder(cur);
    const b = opOrder(op);
    const newer =
      Number.isFinite(b) && (!Number.isFinite(a) || b > a)
        ? true
        : Number.isFinite(a) && Number.isFinite(b) && b === a
          ? (op.attempt ?? 0) > (cur.attempt ?? 0)
          : false;
    if (newer) byChannel.set(op.channel, op);
  }
  const order = ['shopify_blog', 'instagram', 'facebook'];
  return [...byChannel.values()].sort((x, y) => {
    const ix = order.indexOf(x.channel);
    const iy = order.indexOf(y.channel);
    return (ix < 0 ? order.length : ix) - (iy < 0 ? order.length : iy);
  });
}

/** 和服务端汇总器同一条规则(契约 §1 pieceState)。 */
export type PublishPieceState = 'publishing' | 'published' | 'needs_attention';

/**
 * 契约 v1.2 §1:汇总只算**当前修订**的 op。每篇稿件(按草稿行 id,没有就按 clientDraftId)只留
 * draftRevision 最大的那一批;draftRevision 为 null / 0(没有草稿行的发布、老数据)的不过滤。
 * 草稿改过之后,旧修订上的结果(包括 failed)不再算数。
 */
export function currentRevisionOps(ops: PublishOp[]): PublishOp[] {
  const keyOf = (o: PublishOp) => o.draftId ?? o.clientDraftId ?? null;
  const maxRev = new Map<string, number>();
  for (const o of ops) {
    const k = keyOf(o);
    const r = o.draftRevision ?? 0;
    if (!k || r <= 0) continue;
    if (r > (maxRev.get(k) ?? 0)) maxRev.set(k, r);
  }
  if (maxRev.size === 0) return ops;
  return ops.filter(o => {
    const k = keyOf(o);
    const r = o.draftRevision ?? 0;
    if (!k || r <= 0) return true;
    return r === maxRev.get(k);
  });
}

/**
 * 一篇稿件的发布汇总,与 chatbot-api PublishOutcomeAggregator.summarise 同口径:
 * 只算当前修订(见 currentRevisionOps),去掉 cancelled(未执行就取消,不算任何结果),
 * 再按渠道取最新一条 ——
 * 有在途的 → publishing;否则有 failed / unconfirmed → needs_attention;
 * 否则(每个渠道最新一条都是 succeeded 或 abandoned)→ published。
 * 全是 cancelled(或没有 op)→ null。
 *
 * 传这一篇的全部 op(不要先取 latestOpPerChannel):修订和 cancelled 要在取最新之前过滤,
 * 和服务端一致。
 */
export function summarizePublishOps(ops: PublishOp[]): PublishPieceState | null {
  const latest = latestOpPerChannel(currentRevisionOps(ops).filter(o => o.status !== 'cancelled'));
  if (latest.length === 0) return null;
  if (latest.some(o => o.status === 'requested' || o.status === 'executing')) return 'publishing';
  if (latest.some(o => o.status === 'failed' || o.status === 'unconfirmed'))
    return 'needs_attention';
  return latest.every(o => o.status === 'succeeded' || o.status === 'abandoned')
    ? 'published'
    : null;
}

/**
 * 卡片上那颗「Approve & Publish」该不该亮 —— 按渠道看最新那条 op。
 *
 * - `published`:最新一条 succeeded。服务端已经发出去了,卡片并入已发布态(View post 用 resultUrl),
 *   再点一次就是重复发帖。
 * - `in_flight`:requested / executing,正在发,禁用。
 * - `unconfirmed`:不知道发没发出去,先让商家在进度块里点 It's live / Not live,禁用。
 * - `open`:没有 op,或最新一条是 failed(含商家点了 Not live、契约 v1.2 的 not_started)/
 *   abandoned —— 可以(再)发。
 *   cancelled 是未执行就取消、什么都没发出去,也算 open(本批没有产生它的入口)。
 */
export type PublishGate =
  | { state: 'open' }
  | { state: 'in_flight' }
  | { state: 'unconfirmed' }
  | { state: 'published'; resultUrl: string | null };

const OPEN_GATE: PublishGate = { state: 'open' };

/**
 * 契约 v1.2 §1:`not_started` —— status=failed:op 在 requested 里超时、从没被执行器
 * 认领,**确定什么都没发出去**。前端按失败对待:显示为失败、可以重发、可以放弃。
 */
export function isNotStartedOp(op: PublishOp): boolean {
  return op.status === 'failed' && op.errorCode === 'not_started';
}

/**
 * 这条失败要商家去 Shopify 重新授权 —— chatbot-api `PublishErrorClassifier` 把 ShopifyScopeException
 * 记成 `scope_missing`。只按错误码判,不看文案。
 */
export function needsShopifyReauthorize(op: PublishOp): boolean {
  return op.status === 'failed' && op.errorCode === 'scope_missing';
}

/** 卡片上的平台名 → op 的 channel(契约 §1 的别名规则);认不出返回 null。 */
export function publishChannelOf(
  platform: string | null | undefined,
  contentType?: string | null
): string | null {
  const p = (platform ?? '').trim().toLowerCase();
  if (p.includes('instagram') || p === 'ig') return 'instagram';
  if (p.includes('facebook') || p === 'fb') return 'facebook';
  if (p.includes('shopify') || p.includes('blog') || contentType === 'article')
    return 'shopify_blog';
  return null;
}

/**
 * 一篇稿件(某个渠道)的发布闸门。`ops` 是已经按卡认领到的 op;`draftId` 给了就只看这一篇的,
 * `channel` 给了就只看这个渠道的。渠道认不出时看全部渠道,取最保守的那个结论。
 */
export function publishGateFor(
  ops: PublishOp[],
  { draftId, channel }: { draftId?: string | null; channel?: string | null } = {}
): PublishGate {
  let mine = draftId ? ops.filter(op => publishOpDraftKeys(op).includes(draftId)) : ops;
  if (channel) mine = mine.filter(op => op.channel === channel);
  const latest = latestOpPerChannel(mine);
  if (latest.length === 0) return OPEN_GATE;
  if (latest.some(o => o.status === 'requested' || o.status === 'executing'))
    return { state: 'in_flight' };
  if (latest.some(o => o.status === 'unconfirmed')) return { state: 'unconfirmed' };
  const done = latest.find(o => o.status === 'succeeded');
  if (done) return { state: 'published', resultUrl: done.resultUrl ?? null };
  return OPEN_GATE;
}

/** 匹配某张卡(按它的 draft id 们)的 op。 */
export function opsForDraftKeys(
  state: PublishOpsState,
  keys: (string | null | undefined)[]
): PublishOp[] {
  const wanted = new Set(keys.filter((k): k is string => !!k));
  if (wanted.size === 0) return [];
  return Object.values(state).filter(op => publishOpDraftKeys(op).some(k => wanted.has(k)));
}

/**
 * 没有卡可挂的 op,按稿件分组(同一篇的几个渠道放一起),新的组在后。
 */
export function unmatchedPublishOpGroups(
  state: PublishOpsState,
  cardDraftKeys: Set<string>
): { key: string; title: string | null; ops: PublishOp[] }[] {
  const groups = new Map<string, PublishOp[]>();
  for (const op of Object.values(state)) {
    if (publishOpDraftKeys(op).some(k => cardDraftKeys.has(k))) continue;
    const key = op.clientDraftId ?? op.draftId ?? op.pieceId ?? op.requestId ?? op.title ?? op.id;
    const list = groups.get(key) ?? [];
    list.push(op);
    groups.set(key, list);
  }
  return [...groups.entries()]
    .map(([key, ops]) => ({
      key,
      title: ops.find(o => o.title)?.title ?? null,
      ops,
      at: Math.min(...ops.map(o => opOrder(o)).filter(Number.isFinite)),
    }))
    .sort((a, b) => (Number.isFinite(a.at) ? a.at : 0) - (Number.isFinite(b.at) ? b.at : 0))
    .map(({ key, title, ops }) => ({ key, title, ops }));
}
